import { Link, useParams } from "react-router-dom";
import { useEffect, useState, useRef, type ReactNode } from "react";
import { parseAsInteger, useQueryState } from "nuqs";
import { useQueries } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { motion, AnimatePresence } from "motion/react";
import { CalendarOff, CircleAlertIcon, CircleCheckIcon } from "lucide-react";
import { toast } from "sonner";
import { posthog } from "posthog-js";

import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useTheme } from "../../components/ui/theme-provider";
import { WhitePenciledLogo, StyledPenciledTextLogo } from "../../styles/HeaderStyles";
import { PlanOfCareDetails, usePlanOfCareDetails } from "../../hooks/usePlanOfCareDetails";
import { PlanOfCareAvailability } from "../../hooks/usePlanOfCareVisitAvailability";
import { usePlanOfCareStore } from "../../stores/planOfCareStore";
import { convertE164ToReadable } from "../../utils/utils";

import { DetailsSection } from "./DetailsSection";
import { AppointmentsSection } from "./AppointmentsSection";
import { ScheduleSection } from "./ScheduleSection";
import { CancelView } from "./CancelView";
import { PlanOfCareScanAnimation } from "./PlanOfCareAnimation";
import { ProviderAvailabilityAnimation } from "./ProviderAvailbilityAnimation";

const MAX_BATCHES = 2;
const WEEKS_PER_BATCH = 4;
const WEEKS_SEARCH_LIMIT = 40;
const AVAILABILITY_CACHE_TIME = 1000 * 60 * 5;
const SLIDE_ANIMATION = {
  duration: 1,
  ease: [0.32, 0.72, 0, 1] as const,
};

type Period = PlanOfCareDetails["planOfCares"][0]["periods"][0];
type WeekToFetch = { start: string; end: string; appointmentTypeId: string };

const isPeriodOverlapping = (period: Period, startOfWeek: DateTime, endOfWeek: DateTime): boolean => {
  const periodStart = DateTime.fromISO(period.periodStart);
  const periodEnd = DateTime.fromISO(period.periodEnd);
  return periodStart <= endOfWeek && periodEnd >= startOfWeek;
};

const isPeriodContainingDate = (period: Period, date: DateTime): boolean => {
  const periodStart = DateTime.fromISO(period.periodStart);
  const periodEnd = DateTime.fromISO(period.periodEnd);
  return periodStart <= date && periodEnd >= date;
};

const findPeriodIndexForDate = (periods: Period[], date: DateTime): number => {
  return periods.findIndex((period) => isPeriodContainingDate(period, date));
};

const findPeriodIndexForAvailability = (periods: Period[], availabilityStart: string, availabilityEnd: string): number => {
  const startDateTime = DateTime.fromISO(availabilityStart);
  const endDateTime = DateTime.fromISO(availabilityEnd);
  
  let index = periods.findIndex(period => {
    const periodStart = DateTime.fromISO(period.periodStart);
    const periodEnd = DateTime.fromISO(period.periodEnd);
    return periodStart <= startDateTime && periodEnd >= endDateTime;
  });

  if (index === -1 && startDateTime.isValid) {
    const slotDate = startDateTime.startOf("day");
    index = periods.findIndex(period => {
      const periodStart = DateTime.fromISO(period.periodStart).startOf("day");
      const periodEnd = DateTime.fromISO(period.periodEnd).startOf("day");
      return slotDate >= periodStart && slotDate <= periodEnd;
    });
  }

  return index;
};

const findEarliestAvailability = (queries: Array<{ data?: PlanOfCareAvailability }>) => {
  let firstAvailability = null;
  let earliestDateTime = null;

  for (const query of queries) {
    const data = query.data;
    if (!data?.availability) continue;

    for (const providerId in data.availability) {
      const sortedDates = Object.keys(data.availability[providerId]).sort();

      for (const date of sortedDates) {
        const slots = data.availability[providerId][date];
        if (slots.length > 0) {
          const slotDateTime = DateTime.fromISO(slots[0].startISO);

          if (!earliestDateTime || slotDateTime < earliestDateTime) {
            earliestDateTime = slotDateTime;
            firstAvailability = slots[0];
          }
        }
      }
    }
  }

  return firstAvailability;
};

const determineSearchStartDate = (currentDate: DateTime, firstPeriodStart: DateTime): DateTime => {
  return firstPeriodStart > currentDate ? firstPeriodStart : currentDate;
};

const buildWeeksToFetch = (selectedPOC: PlanOfCareDetails["planOfCares"][0], searchStartDate: DateTime, currentDate: DateTime, currentBatch: number): WeekToFetch[] => {
  const weeks: WeekToFetch[] = [];
  let weeksChecked = 0;
  let weekOffset = currentBatch * WEEKS_PER_BATCH;
  const lastPeriodEnd = DateTime.fromISO(selectedPOC.periods[selectedPOC.periods.length - 1].periodEnd);

  while (weeks.length < WEEKS_PER_BATCH && weeksChecked < WEEKS_SEARCH_LIMIT) {
    const startOfWeek = searchStartDate.startOf("week", { useLocaleWeeks: true }).plus({ weeks: weekOffset });
    const endOfWeek = startOfWeek.endOf("week", { useLocaleWeeks: true });

    if (startOfWeek > lastPeriodEnd) break;

    const pocPeriod = selectedPOC.periods.find((period) => isPeriodOverlapping(period, startOfWeek, endOfWeek));

    if (pocPeriod) {
      for (const appointment of pocPeriod.appointments || []) {
        if (appointment.status === "unscheduled") {
          weeks.push({
            start: currentDate > startOfWeek ? currentDate.toFormat("yyyy-MM-dd") : startOfWeek.toFormat("yyyy-MM-dd"),
            end: endOfWeek.toFormat("yyyy-MM-dd"),
            appointmentTypeId: appointment.appointmentTypeId,
          });
        }
      }
    }

    weekOffset++;
    weeksChecked++;
  }

  return weeks;
};

const setDefaultWeek = (periods: Period[], currentDate: DateTime, setWeek: (week: number) => void): void => {
  const periodIndex = findPeriodIndexForDate(periods, currentDate);

  if (periodIndex !== -1) {
    setWeek(periodIndex + 1);
  } else if (periods.length > 0) {
    setWeek(1);
  }
};

const ErrorState = ({ message }: { message?: string }) => (
  <main className="p-8 min-h-svh bg-accent dark:bg-background flex flex-col items-center justify-center">
    <Card>
      <CardContent>
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center justify-center size-12 rounded-full bg-destructive/10 mb-8">
            <CircleAlertIcon className="size-6 text-destructive stroke-[1.75]" />
          </div>
          <h1 className="header-2 mb-4 text-center">{message || "Error loading appointment availability"}</h1>
          <p className="text-base text-muted-foreground text-center">Please try again or contact the clinic for help.</p>
        </div>
      </CardContent>
    </Card>
  </main>
);

const ArchivedState = ({ phoneNumber }: { phoneNumber?: string }) => (
  <main className="p-8 min-h-svh bg-accent dark:bg-background flex flex-col items-center justify-center">
    <Card>
      <CardContent>
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center justify-center size-12 rounded-full bg-blue-500/10 mb-8">
            <CircleAlertIcon className="size-6 text-blue-500 stroke-[1.75]" />
          </div>
          <h1 className="header-2 mb-4 text-center">Please Contact the Clinic</h1>
          <p className="text-base text-muted-foreground text-center max-w-md">
            Online scheduling is currently unavailable for your account. Please contact the clinic at {convertE164ToReadable(phoneNumber || "")} for assistance.
          </p>
        </div>
      </CardContent>
    </Card>
  </main>
);

const AgentOffState = ({ phoneNumber }: { phoneNumber?: string }) => (
  <main className="p-8 min-h-svh bg-accent dark:bg-background flex flex-col items-center justify-center">
    <Card>
      <CardContent>
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center justify-center size-12 rounded-full bg-muted mb-8">
            <CalendarOff className="size-6 text-muted-foreground stroke-[1.75]" />
          </div>
          <h1 className="header-2 mb-4 text-center">Booking Unavailable</h1>
          <p className="text-base text-muted-foreground text-center max-w-md">
            Online booking through this link isn't available right now. Please contact the clinic at {convertE164ToReadable(phoneNumber || "")} to schedule your appointment.
          </p>
        </div>
      </CardContent>
    </Card>
  </main>
);

const DischargedState = ({ detailsData, selectedPOC, setPoc }: { detailsData: PlanOfCareDetails; selectedPOC?: PlanOfCareDetails["planOfCares"][0]; setPoc: (index: number) => void }) => (
  <main className="p-8 min-h-svh bg-accent dark:bg-background flex flex-col items-center justify-center">
    <Card>
      <CardContent>
        <div className="flex flex-col items-center justify-center">
          <div className="flex items-center justify-center size-12 rounded-full bg-emerald-500/10 mb-8">
            <CircleCheckIcon className="size-6 text-emerald-500 stroke-[1.75]" />
          </div>
          <h1 className="header-2 mb-4 text-center">Treatment Complete!</h1>
          <p className="text-base text-muted-foreground text-center max-w-md">
            Your provider has determined you've successfully completed your care. If you have any questions, please contact the clinic at{" "}
            {convertE164ToReadable(detailsData.organization.phoneNumber || "")}.
          </p>
          {detailsData.planOfCares.length > 1 && (
            <div className="mt-6 flex items-center gap-2 text-sm font-medium text-muted-foreground">
              <span>Case Study:</span>
              <Select
                value={selectedPOC?.caseStudyId?.toString() || ""}
                onValueChange={(value) => {
                  const planOfCareIndex = detailsData.planOfCares.findIndex((pocItem: PlanOfCareDetails["planOfCares"][0]) => pocItem.caseStudyId?.toString() === value);
                  if (planOfCareIndex !== -1) setPoc(planOfCareIndex);
                }}
              >
                <SelectTrigger aria-label="Select Case Study" className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {detailsData.planOfCares.map((planOfCare) => (
                    <SelectItem key={planOfCare.caseStudyId} value={planOfCare.caseStudyId?.toString() || ""}>
                      {planOfCare.caseStudyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  </main>
);

const LoadingState = ({ weeksToFetch }: { weeksToFetch: WeekToFetch[] }) => {
  const isSearchingAvailability = weeksToFetch.length > 0;
  const startDate = isSearchingAvailability ? DateTime.fromFormat(weeksToFetch[0].start, "yyyy-MM-dd").toFormat("MMM d") : "";
  const endDate = isSearchingAvailability ? DateTime.fromFormat(weeksToFetch[weeksToFetch.length - 1].end, "yyyy-MM-dd").toFormat("MMM d") : "";

  return (
    <main className="p-8 min-h-svh bg-background flex flex-col items-center justify-center text-foreground overflow-hidden">
      <AnimatePresence mode="popLayout" initial={false}>
        {!isSearchingAvailability ? (
          <motion.div
            key="plan-of-care"
            initial={{ x: 0 }}
            exit={{
              x: "-100%",
              transition: SLIDE_ANIMATION,
            }}
            className="w-full absolute flex justify-center p-6"
          >
            <div className="max-w-md">
              <h1 className="header-2 mb-4 text-pretty">Just a moment while we review your plan of care</h1>
              <p className="text-sm text-muted-foreground">We're analyzing your treatment requirements and care needs.</p>
              <div className="mt-6">
                <PlanOfCareScanAnimation />
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="provider-availability"
            initial={{ x: "100%" }}
            animate={{
              x: 0,
              transition: SLIDE_ANIMATION,
            }}
            exit={{ opacity: 0, transition: { duration: 0.5 } }}
            className="w-full absolute flex justify-center p-6"
          >
            <div className="max-w-md">
              <h1 className="header-2 mb-4 text-pretty">Hold tight as we search for the next available times</h1>
              <p className="text-sm text-muted-foreground">
                We're checking for available times between {startDate} and {endDate}.
              </p>
              <div className="mt-6">
                <ProviderAvailabilityAnimation rangeLabel={`${startDate} - ${endDate}`} />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
};

export default function BookPlanOfCare() {
  const { resolvedTheme } = useTheme();
  const { uniqueId } = useParams<{ uniqueId: string }>();

  const [week, setWeek] = useQueryState("week", parseAsInteger);
  const [poc, setPoc] = useQueryState("poc", parseAsInteger);
  const [paymentSuccess, setPaymentSuccess] = useQueryState("payment_success");
  const [cancelVisit] = useQueryState("cancel_visit");
  const [currentDate] = useState(DateTime.now());
  const [archived, setArchived] = useState(false);
  const [selectedPOC, setSelectedPOC] = useState<PlanOfCareDetails["planOfCares"][0]>();
  const [currentBatch, setCurrentBatch] = useState(0);
  const [weeksToFetch, setWeeksToFetch] = useState<WeekToFetch[]>([]);

  const { setUniqueId, setTotalWeeks, setGap, discharged, setDischarged, setAuthorization, setAuthorizationsByType, setScheduledVisitDates, setIgnoreAuthorization, setCaseStudyId, setPlanOfCareId, planOfCareId, setCrossCaseStudyBlockedDates, bookingOutcomes } = usePlanOfCareStore();

  const selectedPOCRef = useRef(selectedPOC);
  const detailsDataRef = useRef<PlanOfCareDetails>();
  const weeksToFetchRef = useRef(weeksToFetch);
  const currentBatchRef = useRef(currentBatch);

  useEffect(() => {
    selectedPOCRef.current = selectedPOC;
  }, [selectedPOC]);
  useEffect(() => {
    weeksToFetchRef.current = weeksToFetch;
  }, [weeksToFetch]);
  useEffect(() => {
    currentBatchRef.current = currentBatch;
  }, [currentBatch]);

  useEffect(() => {
    if (uniqueId) setUniqueId(uniqueId);
  }, [uniqueId, setUniqueId]);

  const {
    data: detailsData,
    error: detailsError,
    isLoading: isLoadingDetails,
    refetch: refetchDetails,
  } = usePlanOfCareDetails({
    uniqueId,
    enabled: Boolean(uniqueId),
  });

  useEffect(() => {
    detailsDataRef.current = detailsData;
  }, [detailsData]);

  useEffect(() => {
    if (!detailsData) return;

    posthog.identify(detailsData.patient.id);
    setArchived(detailsData.patient.archived || false);

    let pocIndex = poc ?? 0;
    if ((poc === null || poc === undefined) && detailsData.planOfCares.length > 1) {
      const selectedIndex = detailsData.planOfCares.findIndex((planOfCare: PlanOfCareDetails["planOfCares"][0]) => planOfCare.selected);
      if (selectedIndex !== -1) pocIndex = selectedIndex;
    }
    setPoc(pocIndex);
  }, [detailsData, poc, setPoc]);

  useEffect(() => {
    if (!detailsData || poc === null || poc === undefined) return;

    const selectedPlanOfCare = detailsData.planOfCares[poc];

    setCurrentBatch(0);
    setSelectedPOC(selectedPlanOfCare);
    setDischarged(selectedPlanOfCare.discharged);
    setTotalWeeks(selectedPlanOfCare.periods.length);
    setGap(selectedPlanOfCare.gap);
    setCaseStudyId(selectedPlanOfCare.caseStudyId?.toString());
    setPlanOfCareId(selectedPlanOfCare.planOfCareId);
    setAuthorization(selectedPlanOfCare.authorization);
    setAuthorizationsByType(selectedPlanOfCare.authorizationsByType);
    setIgnoreAuthorization(selectedPlanOfCare.ignoreAuthorization ?? false);

    const visitDates: string[] = [];
    for (const appointment of selectedPlanOfCare.appointments ?? []) {
      if (appointment.apptStatus !== "NoSh" && appointment.apptStatus !== "Canc" && appointment.start) {
        const dateKey = DateTime.fromISO(appointment.start).toISODate();
        if (dateKey) visitDates.push(dateKey);
      }
    }
    setScheduledVisitDates(visitDates);

    // Compute cross-case study blocked dates (dates where OTHER case studies have appointments)
    // This prevents booking multiple appointments on the same day across different case studies.
    // When the organization allows same-day appointments across provider groups, this
    // restriction is skipped entirely - the backend's availability response already excludes
    // providers that would conflict (i.e. providers in the same provider group).
    const blockedDates = new Set<string>();
    const currentCaseStudyId = selectedPlanOfCare.caseStudyId?.toString();

    if (!detailsData.organization.allowSameDayAppointmentsAcrossProviderGroups) {
      for (let i = 0; i < detailsData.planOfCares.length; i++) {
        // Skip the currently selected case study - its dates are handled separately with gap logic
        if (i === poc) continue;

        const otherPOC = detailsData.planOfCares[i];
        for (const period of otherPOC.periods) {
          // Add scheduled appointments from other case studies
          for (const appointment of period.appointments) {
            if (appointment.status !== "unscheduled" && appointment.start) {
              const dateKey = DateTime.fromISO(appointment.start).toISODate();
              if (dateKey) blockedDates.add(dateKey);
            }
          }

          // Add extra appointments from other case studies
          if (period.extraAppointments) {
            for (const extraAppointment of period.extraAppointments) {
              if (extraAppointment.start) {
                const dateKey = DateTime.fromISO(extraAppointment.start).toISODate();
                if (dateKey) blockedDates.add(dateKey);
              }
            }
          }
        }
      }

      // Also add dates from bookingOutcomes in the current session that belong to OTHER case studies
      for (const outcome of bookingOutcomes) {
        if (outcome.start && outcome.caseStudyId && outcome.caseStudyId !== currentCaseStudyId) {
          const dateKey = outcome.start.toISODate();
          if (dateKey) blockedDates.add(dateKey);
        }
      }
    }

    setCrossCaseStudyBlockedDates(blockedDates);
  }, [detailsData, poc, setDischarged, setAuthorization, setAuthorizationsByType, setScheduledVisitDates, setIgnoreAuthorization, setTotalWeeks, setGap, setCaseStudyId, setPlanOfCareId, setCrossCaseStudyBlockedDates, bookingOutcomes]);

  useEffect(() => {
    if (!selectedPOC || week !== null || cancelVisit !== null) return;

    const firstPeriodStart = DateTime.fromISO(selectedPOC.periods[0].periodStart);
    const searchStartDate = determineSearchStartDate(currentDate, firstPeriodStart);
    const weeks = buildWeeksToFetch(selectedPOC, searchStartDate, currentDate, currentBatch);

    if (weeks.length === 0) {
      setDefaultWeek(selectedPOC.periods, currentDate, setWeek);
    } else {
      setWeeksToFetch(weeks);
    }
  }, [currentBatch, selectedPOC, currentDate, week, setWeek, cancelVisit]);

  const queries = useQueries({
    queries: weeksToFetch.map((weekData) => ({
      queryKey: ["plan-of-care-visit-availability", uniqueId, weekData.end, weekData.appointmentTypeId, planOfCareId],
      queryFn: async () => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/bookings/plan-of-care/availability`;
        const params = new URLSearchParams({
          uniqueId: uniqueId || "",
          startDate: weekData.start,
          endDate: weekData.end,
          appointmentTypeId: weekData.appointmentTypeId,
        });
        if (planOfCareId) {
          params.append("planOfCareId", planOfCareId);
        }

        const response = await fetch(`${url}?${params}`, { method: "GET" });

        if (response.status !== 200) {
          const isClientError = [404, 400, 410].includes(response.status);
          if (isClientError) {
            throw new Error((await response.json()).message);
          }
          throw new Error("Error loading appointment availability");
        }

        return response.json();
      },
      retry: false,
      staleTime: AVAILABILITY_CACHE_TIME,
      enabled: week === null && currentBatch < MAX_BATCHES && weeksToFetch.length > 0,
    })),
  });

  useEffect(() => {
    const currentSelectedPOC = selectedPOCRef.current;
    const currentDetailsData = detailsDataRef.current;
    const currentWeeksToFetch = weeksToFetchRef.current;
    const currentBatchValue = currentBatchRef.current;

    const shouldProcessQueries =
      week === null && currentSelectedPOC && currentDetailsData && currentWeeksToFetch.length > 0 && queries.length === currentWeeksToFetch.length && queries.every((query) => !query.isLoading);

    if (!shouldProcessQueries) return;

    const firstAvailability = findEarliestAvailability(queries);

    if (!firstAvailability) {
      if (currentBatchValue < MAX_BATCHES - 1) {
        setCurrentBatch(currentBatchValue + 1);
        setWeeksToFetch([]);
        return;
      }

      const periodIndex = findPeriodIndexForDate(currentSelectedPOC.periods, currentDate);
      const weekNumber = periodIndex !== -1 ? periodIndex + 1 : 1;

      setWeek(weekNumber);
      if (currentWeeksToFetch.length > 0) {
        toast.info("Sorry we couldn't find any availability in the next few weeks.", { duration: 3000 });
      }
      setWeeksToFetch([]);
      return;
    }

    const periodIndex = findPeriodIndexForAvailability(currentSelectedPOC.periods, firstAvailability.startISO, firstAvailability.endISO);

    if (periodIndex !== -1) {
      setWeek(periodIndex + 1);
      setWeeksToFetch([]);
    } else {
      setWeek(1);
      setWeeksToFetch([]);
    }
  }, [queries, week, currentDate, setWeek, setCurrentBatch, setWeeksToFetch]);

  const renderWithPaymentSuccessDialog = (content: ReactNode) => (
    <>
      <Dialog
        open={paymentSuccess === "true"}
        onOpenChange={(open) => {
          if (!open) {
            setPaymentSuccess(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Payment successful</DialogTitle>
            <DialogDescription>
              We received your late cancellation fee. You should receive a text confirmation regarding the appointment cancellation.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setPaymentSuccess(null)}>Okay</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {content}
    </>
  );

  if (detailsError || queries.some((query) => query.isError)) {
    return renderWithPaymentSuccessDialog(<ErrorState message={detailsError?.message} />);
  }

  if (detailsData?.agentActive === false) {
    return renderWithPaymentSuccessDialog(<AgentOffState phoneNumber={detailsData.organization.phoneNumber} />);
  }

  if (archived) {
    return renderWithPaymentSuccessDialog(<ArchivedState phoneNumber={detailsData?.organization.phoneNumber} />);
  }

  if (discharged && detailsData) {
    return renderWithPaymentSuccessDialog(<DischargedState detailsData={detailsData} selectedPOC={selectedPOC} setPoc={setPoc} />);
  }

  if (cancelVisit && !isLoadingDetails && detailsData) {
    return renderWithPaymentSuccessDialog(
      <main className="xl:p-8 min-h-svh bg-background xl:bg-accent dark:bg-background flex flex-col xl:items-center xl:justify-center w-full">
        <Card className="p-0 border-0 shadow-none bg-transparent xl:border xl:shadow-sm xl:bg-card w-full xl:w-auto xl:max-h-none xl:overflow-visible max-h-svh overflow-y-auto">
          <CardContent className="p-0">
            <div className="flex flex-col xl:flex-row">
              <DetailsSection uniqueId={uniqueId} />
              <CancelView
                uniqueId={uniqueId}
                detailsData={detailsData}
                cancelVisit={cancelVisit}
                onCancelled={refetchDetails}
              />
            </div>
          </CardContent>
        </Card>
      </main>
    );
  }

  if (isLoadingDetails || !selectedPOC || queries.some((q) => q.isLoading) || !week) {
    return renderWithPaymentSuccessDialog(<LoadingState weeksToFetch={weeksToFetch} />);
  }

  return renderWithPaymentSuccessDialog(
    <main className="xl:p-8 min-h-svh bg-background xl:bg-accent dark:bg-background flex flex-col xl:items-center xl:justify-center w-full">
      <Card className="p-0 border-0 shadow-none bg-transparent xl:border xl:shadow-sm xl:bg-card w-full xl:w-auto xl:max-h-none xl:overflow-visible max-h-svh overflow-y-auto">
        <CardContent className="p-0">
          <div className="flex flex-col xl:flex-row">
            <DetailsSection uniqueId={uniqueId} />
            <AppointmentsSection />
            <ScheduleSection />
          </div>
        </CardContent>
      </Card>
      <Link to="https://penciled.com" target="_blank" rel="noreferrer" className="text-xs text-muted-foreground py-6 hidden xl:block">
        {resolvedTheme === "light" ? <StyledPenciledTextLogo className="!w-28 hidden xl:block" /> : <WhitePenciledLogo className="!w-28 hidden xl:block" />}
      </Link>
    </main>
  );
}
